/**
 * RodeoBuddyGroupsPanel — permanent rodeo-wide buddy-group inspection
 * panel rendered above the per-event rows on the Manage Draws list.
 *
 * Why it exists: buddy groups span events, but the per-event draw page
 * only knows about its own placements. Without a rodeo-level view an
 * admin can't easily verify that every buddy group landed in a single
 * performance — they'd have to open each event in turn. This panel
 * surfaces the entire roster + placements per group, with a strong color
 * cue when a group's buddy-group entries are split across performances
 * (the invariant: all buddy-group entries should share one performance
 * across every event in the rodeo).
 *
 * Each entry within an expanded group carries an "Open" button that
 * jumps directly to the right event's per-event draw page via the
 * onOpenEvent callback the parent provides.
 */
import { useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Users,
  ListChecks,
  ExternalLink,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useRodeoBuddyGroupsOverview,
  type BuddyGroupOverviewItem,
} from '@/api/extensions/useDrawOps';

interface RodeoBuddyGroupsPanelProps {
  rodeoId: string;
  onOpenEvent: (eventId: string) => void;
}

export function RodeoBuddyGroupsPanel({
  rodeoId,
  onOpenEvent,
}: RodeoBuddyGroupsPanelProps) {
  const { data, isLoading, error } = useRodeoBuddyGroupsOverview(rodeoId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Section is collapsed by default — admins typically don't need to see
  // every buddy group every time. The header always renders so the
  // split-performance warning count is visible from the get-go.
  const [sectionOpen, setSectionOpen] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }

  if (error || !data) {
    // Don't take up real estate when this is unavailable — the per-event
    // conflict panel still surfaces the same split-performance warning.
    return null;
  }

  const groups = data.buddy_groups;
  if (groups.length === 0) {
    return null;
  }

  const splitCount = groups.filter((g) => g.has_split_performance).length;
  const hasAnyWarning = splitCount > 0;

  const toggle = (groupId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  return (
    <div
      className={`rounded-md border ${
        hasAnyWarning
          ? 'border-red-400 bg-red-50/60 dark:bg-red-950/15 dark:border-red-900/60'
          : 'border-border bg-card/40'
      }`}
    >
      <button
        type="button"
        onClick={() => setSectionOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <div className="flex items-center gap-2">
          {sectionOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">
            Buddy groups ({groups.length})
          </span>
          {hasAnyWarning && (
            <Badge
              variant="destructive"
              className="text-xs flex items-center gap-1"
            >
              <AlertTriangle className="h-3 w-3" />
              {splitCount} split across performances
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {sectionOpen ? 'Hide' : 'Show'}
        </span>
      </button>
      {sectionOpen && (
        <ul className="divide-y divide-border/60 border-t border-border/60">
          {groups.map((group) => (
            <BuddyGroupRow
              key={group.buddy_group_id}
              group={group}
              expanded={expanded.has(group.buddy_group_id)}
              onToggle={() => toggle(group.buddy_group_id)}
              onOpenEvent={onOpenEvent}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface BuddyGroupRowProps {
  group: BuddyGroupOverviewItem;
  expanded: boolean;
  onToggle: () => void;
  onOpenEvent: (eventId: string) => void;
}

function BuddyGroupRow({
  group,
  expanded,
  onToggle,
  onOpenEvent,
}: BuddyGroupRowProps) {
  const isWarning = group.has_split_performance;
  return (
    <li
      className={`p-3 ${
        isWarning ? 'bg-red-50/40 dark:bg-red-950/10' : ''
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 text-left"
      >
        <div className="flex items-start gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`font-medium truncate ${
                  isWarning ? 'text-red-900 dark:text-red-200' : ''
                }`}
              >
                {group.group_name?.trim() || 'Unnamed buddy group'}
              </span>
              {isWarning && (
                <Badge
                  variant="destructive"
                  className="text-[10px] flex items-center gap-1"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Split across {group.performance_ids.length} performances
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {group.member_count} member
                {group.member_count === 1 ? '' : 's'}
              </span>
              <span className="flex items-center gap-1">
                <ListChecks className="h-3 w-3" />
                {group.entry_count} entr{group.entry_count === 1 ? 'y' : 'ies'}
                {group.placed_entry_count > 0 &&
                  ` · ${group.placed_entry_count} placed`}
              </span>
              {isWarning && (
                <span className="text-red-700 dark:text-red-300">
                  Performances: {group.performance_names.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="mt-3 ml-6 space-y-3">
          {group.members.length === 0 && (
            <p className="text-xs text-muted-foreground">No members yet.</p>
          )}
          {group.members.map((member) => (
            <div
              key={member.user_profile_id}
              className="rounded border border-border/60 bg-background/60 p-2"
            >
              <div className="text-sm font-medium">{member.display_name}</div>
              {member.entries.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1">
                  No buddy-group entries.
                </p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {member.entries.map((entry) => {
                    // A specific entry is flagged only when this group is
                    // split AND the entry has a placement (i.e. it is one
                    // of the placements the admin needs to reconcile).
                    const flagged = entry.is_split_offender && !!entry.performance_id;
                    return (
                      <li
                        key={entry.registration_id}
                        className={`flex items-center justify-between gap-2 text-xs rounded px-2 py-1.5 ${
                          flagged
                            ? 'bg-red-100/70 dark:bg-red-950/30 border border-red-300/70 dark:border-red-900/40'
                            : 'bg-muted/30'
                        }`}
                      >
                        <div className="min-w-0">
                          <span
                            className={`font-medium ${
                              flagged ? 'text-red-900 dark:text-red-200' : ''
                            }`}
                          >
                            {entry.event_name}
                          </span>
                          <span className="text-muted-foreground">
                            {entry.performance_name
                              ? ` → ${entry.performance_name}`
                              : ' · not placed'}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs shrink-0"
                          onClick={() => onOpenEvent(entry.event_id)}
                        >
                          Open
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
